"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Moon, Sun, HelpCircle, User as UserIcon, LogOut, ChevronDown } from "lucide-react";
import client from "@/lib/api/client";
import { _ } from "@/lib/api/i18n";
import { activeCtx, dataCtx, user_ref } from "@/lib/api/state";
import { useLayout } from "@/lib/context/layout";
import { AvatarElement } from "./Avatar";
import { LangPicker } from "./LangPicker";

export const UserDetailsElement = () => {
    const {
        userState,
        setUserState,
        setLanguage,
        refreshDashboards,
        setRefreshDashboards,
        dataContext,
        setDataContext,
    } = useLayout();
    const [state, setState] = useState<Record<string, unknown> & { name: string; loaded?: boolean; firstName?: string; lastName?: string; role?: string; lang?: string; theme?: string; avatar?: string }>({ name: "Loading" });
    const [ephemeral, setEphemeral] = useState<Record<string, unknown> & { name: string; firstName?: string; lastName?: string; lang?: string; theme?: string; avatar?: string }>({ name: "Loading" });
    const [formVisible, setFormVisible] = useState(false);
    const [version, setVersion] = useState("Loading");
    const fileRef = useRef<HTMLInputElement>(null);

    const toggleUserForm = () => {
        if (formVisible) {
            setEphemeral(state);
        }
        setFormVisible(!formVisible);
    };

    const toggleTheme = () => {
        client.toggle_theme().then(() => {
            setRefreshDashboards(!refreshDashboards);
            const theme = userState.theme === "bright" ? "dark" : "bright";
            const new_user = { ...userState, theme };
            (user_ref as { user: unknown }).user = new_user;
            setUserState(new_user as typeof userState);
            document.documentElement.classList.toggle("dark");
            localStorage.setItem("theme", theme);
        });
    };

    const help_page = (activeCtx.properties as Record<string, { value: string }>)?.help_page;
    const help_redirect = help_page ? help_page.value : null;

    const didMount = () => {
        if ((state as { loaded?: boolean }).loaded) {
            return;
        }
        Promise.all([client.get_user_details(), client.get_version()]).then((rs) => {
            const resp = rs[0] as Record<string, unknown> & { firstName: string; lastName: string; lang: string; loaded?: boolean; name?: string };
            const resp2 = rs[1] as { version: string };
            resp.loaded = true;
            resp.name = `${resp.firstName.toLocaleUpperCase()} ${resp.lastName.toLocaleUpperCase()}`;
            dataCtx.lang = resp.lang;
            client.get_tokens().then((data: Record<string, unknown> & { isLoaded?: boolean }) => {
                data.isLoaded = true;
                setLanguage(data);
                (user_ref as { user: unknown }).user = resp;
                setVersion(resp2.version);
                setState(resp as typeof state);
                setEphemeral(resp as typeof ephemeral);
                setUserState(resp as unknown as typeof userState);
                setRefreshDashboards(!refreshDashboards);
            });
        });
    };

    useEffect(() => {
        if (userState.loaded) {
            return;
        }
        didMount();
    }, [userState]);

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>, name: string) => {
        const val = e.target.value || "";
        const ephemeralUser = { ...ephemeral };
        ephemeralUser[name] = val;
        ephemeralUser.name = `${ephemeralUser.firstName} ${ephemeralUser.lastName}`;
        setEphemeral(ephemeralUser);
    };

    const previewFile = () => {
        const file = fileRef.current?.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        const width = 180;
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                const elem = document.createElement("canvas");
                const scaleFactor = width / img.width;
                elem.width = width;
                elem.height = img.height * scaleFactor;
                const ctx = elem.getContext("2d")!;
                ctx.drawImage(img, 0, 0, width, img.height * scaleFactor);
                ctx.canvas.toBlob(
                    (blob) => {
                        const f = new File([blob!], "avatar-file", {
                            type: "image/jpeg",
                            lastModified: Date.now(),
                        });
                        const reader2 = new FileReader();
                        reader2.onloadend = () => {
                            setEphemeral((ef) => ({ ...ef, avatar: reader2.result as string }));
                        };
                        reader2.readAsDataURL(f);
                    },
                    "image/jpeg",
                    1
                );
            };
        };
    };

    if (!(state as { loaded?: boolean }).loaded) {
        return <Skeleton className="mr-2 ml-3 h-10 w-10 rounded-full" />;
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger
                    className="flex items-center gap-2 cursor-pointer h-10 px-3 rounded-full border border-border bg-transparent hover:bg-accent hover:border-muted-foreground/30 transition-colors"
                >
                    <AvatarElement size={2.5} />
                    <span className="text-sm">{state.name}</span>
                    <ChevronDown className="h-3 w-3 text-muted-foreground" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <div className="flex flex-col items-center p-3 pointer-events-none">
                        <AvatarElement size={4.8} />
                        <span className="font-bold mt-1">{state.firstName as string}</span>
                        <span className="font-bold">{state.lastName as string}</span>
                        <span className="text-sm text-muted-foreground">{_(state.role as string)}</span>
                    </div>
                    <DropdownMenuSeparator />
                    <div className="flex items-center justify-between p-2 px-3">
                        <div className="flex items-center gap-2">
                            {userState.theme === "bright" ? (
                                <Sun className="h-4 w-4 text-muted-foreground" />
                            ) : (
                                <Moon className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="text-sm">{_("Dark Mode")}</span>
                        </div>
                        <Switch
                            checked={userState.theme === "dark"}
                            onCheckedChange={toggleTheme}
                        />
                    </div>
                    <DropdownMenuSeparator />
                    {help_redirect && (
                        <DropdownMenuItem onClick={() => window.open(help_redirect, "_blank")}>
                            <HelpCircle className="h-4 w-4 mr-2" />
                            {_("Help page")}
                        </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={toggleUserForm}>
                        <UserIcon className="h-4 w-4 mr-2" />
                        {_("Update profile")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => client.do_logout()}>
                        <LogOut className="h-4 w-4 mr-2" />
                        {_("Logout")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <div className="flex flex-col items-center p-3 pointer-events-none">
                        <span className="text-xs">Front-end version:</span>
                        <span className="text-xs">0.0.0-local</span>
                        <span className="text-xs">Back-end version:</span>
                        <span className="text-xs">{version}</span>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={formVisible} onOpenChange={setFormVisible}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{_("Update User Details")}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div
                            className="mx-auto cursor-pointer"
                            style={{ width: "18rem" }}
                            onClick={() => fileRef.current?.click()}
                        >
                            <AvatarElement size={18} avatar={ephemeral.avatar as string} name={ephemeral.name} />
                            <input
                                ref={fileRef}
                                type="file"
                                onChange={previewFile}
                                id="avatar-file"
                                style={{ display: "none" }}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="firstName">{_("First Name")}</Label>
                            <Input
                                id="firstName"
                                value={(ephemeral.firstName as string) || ""}
                                onChange={(e) => onInputChange(e, "firstName")}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="lastName">{_("Last Name")}</Label>
                            <Input
                                id="lastName"
                                value={(ephemeral.lastName as string) || ""}
                                onChange={(e) => onInputChange(e, "lastName")}
                                required
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>{_("Language")}</Label>
                            <LangPicker
                                initial={ephemeral.lang as string}
                                onChange={(e) => setEphemeral((ep) => ({ ...ep, lang: e }))}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Checkbox
                                id="DarkTheme"
                                checked={ephemeral.theme === "dark"}
                                onCheckedChange={(checked) =>
                                    setEphemeral((ef) => ({ ...ef, theme: checked ? "dark" : "bright" }))
                                }
                            />
                            <Label htmlFor="DarkTheme" className="mb-0">
                                {_("Dark Theme")}
                            </Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={toggleUserForm}>
                            {_("Cancel")}
                        </Button>
                        <Button
                            onClick={() => {
                                client.set_user_details(ephemeral).then(() => {
                                    dataCtx.lang = ephemeral.lang as string;
                                    setDataContext({ ...dataContext, lang: ephemeral.lang as string });
                                    setState((us) => ({ ...us, loaded: false }));
                                    setUserState((us) => ({ ...us, loaded: false }));
                                    toggleUserForm();
                                });
                            }}
                        >
                            {_("Save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
